import { Navigation, PatientAppointmentConsentModal } from './components'
import { Button, Loader, Table, Title } from '@mantine/core'
import { Th } from '@/components'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useFhirSearch } from '@/hooks'
import { useEffect, useRef, useState } from 'react'
import { Consent, DocumentReference } from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { formatDateTime } from '@/utils.ts'
import { logError } from '@/utils/errorUtils'
import { textToPdf } from '@/utils/textToPdf.ts'
import { IconDownload } from '@tabler/icons-react'
import { MouseEvent } from 'react'

const renderColumn = (resource: Consent, itemCode: string) => {
  switch (itemCode) {
    case 'status':
      return (
        <div
          className={
            resource.status === 'active'
              ? 'text-green-700'
              : resource.status === 'rejected'
                ? 'text-red-700'
                : 'text-orange-700'
          }
        >
          {resource.status}
        </div>
      )

    case 'scope':
      return (
        resource.scope.coding?.[0]?.display ||
        resource.scope.coding?.[0]?.code ||
        resource.scope.text ||
        ''
      )

    case 'category':
      return (
        resource.category?.[0]?.coding?.[0]?.display ||
        resource.category?.[0]?.coding?.[0]?.code ||
        resource.category?.[0]?.text ||
        ''
      )

    case 'date':
      return formatDateTime(resource.dateTime || '', '-', true)

    case 'patient':
      return resource.patient?.display || resource.patient?.reference || ''

    case 'performer':
      return resource.performer?.map((item) => item.display || item.reference).join(', ') || ''

    case 'organization':
      return resource.organization?.map((item) => item.display || item.reference).join(', ') || ''

    default:
      return itemCode
  }
}
type Attachment = {
  title?: string
  url?: string
  data?: string
  contentType?: string
}

export function ConsentsContracts() {
  const [consents, setConsents] = useState<Consent[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [tableHeadTop, setTableHeadTop] = useState('9rem')
  const titleBarRef = useRef<HTMLDivElement | null>(null)
  const { search, getSearchQuery, getAvailableFields, getAvailableSorting, loadMore, setSort } =
    useFhirSearch('Consent')
  const medplum = useMedplum()

  useEffect(() => {
    const searchQuery = getSearchQuery()
    if (medplum) {
      const query = searchQuery
        ? `${searchQuery}&_include=Consent:source-reference`
        : '_include=Consent:source-reference'
      medplum
        .search('Consent', query)
        .then((result) => {
          const consentList =
            result.entry
              ?.filter((e) => e.resource?.resourceType === 'Consent')
              .map((entry) => entry.resource as Consent) || []
          const documentRefs =
            result.entry
              ?.filter(
                (e) =>
                  (e.resource as never as DocumentReference)?.resourceType === 'DocumentReference',
              )
              .map((entry) => entry.resource as never as DocumentReference) || []

          for (const consent of consentList || []) {
            const docId = consent.sourceReference?.reference?.split('/').pop()
            if (!docId) continue

            const doc = documentRefs.find((item) => item.id === docId)
            if (!doc) continue

            consent.sourceReference!.resource = doc
          }
          if (search.offset === 0) {
            setConsents(consentList)
          } else {
            setConsents((prev) => [...prev, ...consentList])
          }
          setHasMore(!!result.link?.find((item) => item.relation === 'next'))
        })
        .catch((e) => {
          logError('super', 'Failed to fetch consents', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch organizations',
            color: 'red',
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, search])

  useEffect(() => {
    const updateOffsets = () => {
      if (!titleBarRef.current) {
        return
      }
      const computedTop = parseFloat(window.getComputedStyle(titleBarRef.current).top)
      const stickyTop = Number.isFinite(computedTop) ? computedTop : 0
      const titleHeight = titleBarRef.current.offsetHeight
      setTableHeadTop(`${stickyTop + titleHeight}px`)
    }

    updateOffsets()
    window.addEventListener('resize', updateOffsets)
    return () => window.removeEventListener('resize', updateOffsets)
  }, [])

  const sorted = (itemCode: string) => {
    return search.sortRules?.find((item) => item.code === itemCode)
  }

  const isSortAvailable = (itemCode: string) => {
    return getAvailableSorting().includes(itemCode)
  }

  const onSort = (itemCode: string): (() => void) | undefined => {
    if (!isSortAvailable(itemCode)) {
      return undefined
    }
    const existSort = sorted(itemCode)

    if (!existSort) {
      return () => setSort(itemCode, 'desc')
    }

    if (existSort.descending) {
      return () => setSort(itemCode, 'asc')
    }

    return () => setSort(itemCode, 'none')
  }

  const availFieldsRaw = getAvailableFields().filter((item) => search.fields?.includes(item.code))
  const availFields = [
    ...['patient', 'category', 'date', 'status']
      .map((key) => availFieldsRaw.find((item) => item.code === key))
      .filter((item): item is (typeof availFieldsRaw)[number] => Boolean(item)),
    ...availFieldsRaw.filter(
      (item) =>
        !['patient', 'category', 'date', 'status', 'scope', 'performer'].includes(item.code),
    ),
  ]
  const handleDownloadConsentDoc = async (
    event: MouseEvent<HTMLButtonElement>,
    attachment: Attachment,
  ) => {
    if (attachment?.data) {
      textToPdf(
        attachment?.data || '',
        `${attachment?.title || 'consent-document'}`.toLowerCase().replace(/\s/g, '-'),
      )
    } else if (attachment?.contentType === 'application/pdf' && attachment?.url) {
      event.preventDefault()
      const res = await fetch(attachment?.url)
      const blob = await res.blob()
      const tempUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = tempUrl
      a.download = `${attachment?.title} Consent Document.pdf`
      a.click()
      URL.revokeObjectURL(tempUrl)
    }
  }
  return (
    <>
      <Navigation />
      <div className="px-6">
        <div
          className="sticky top-[6.25rem] z-20 flex w-full items-center gap-4 bg-white"
          ref={titleBarRef}
        >
          <Title order={2} mb={0}>
            Consents & Contracts
          </Title>
          <PatientAppointmentConsentModal />
        </div>

        <InfiniteScroll
          dataLength={consents.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<Loader color="blue" />}
          scrollableTarget={null}
          style={{ display: 'contents' }}
        >
          <Table className="mt-0">
            <Table.Thead className="bg-[#FAFAFA] text-[#7D7987]">
              <Table.Tr
                className="sticky z-10 h-[44px] !bg-[#FAFAFA]"
                style={{ top: tableHeadTop }}
              >
                {availFields.map((item) => (
                  <Th
                    className="text-sm"
                    key={item.code}
                    reversed={sorted(item.code)?.descending === true}
                    sorted={!!sorted(item.code)}
                    onSort={onSort(item.code)}
                  >
                    {item.title}
                  </Th>
                ))}
                <Th className="text-right text-sm">Actions</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {consents.map((consent) => (
                <Table.Tr key={consent.id} className="h-[44px] cursor-pointer hover:opacity-85">
                  {availFields.map((item) => (
                    <Table.Td key={item.code}>{renderColumn(consent, item.code)}</Table.Td>
                  ))}
                  <Table.Td key="download">
                    {!!(consent.sourceReference?.resource as DocumentReference)?.content?.[0]
                      ?.attachment && (
                      <Button
                        variant="transparent"
                        size="xs"
                        onClick={(e) =>
                          handleDownloadConsentDoc(
                            e,
                            (consent.sourceReference?.resource as DocumentReference)?.content?.[0]
                              ?.attachment,
                          )
                        }
                      >
                        <IconDownload size={20} />
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </InfiniteScroll>
      </div>
    </>
  )
}
